import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { assertDemActorReady, fetchDemUserDisplayNameMap, resolveDemDisplayUser } from './demUserNames';
import { demMainCustomerCode, fetchDemCustomer } from './demCustomers';
import { fetchPricesForItems, getNavisionPrice } from './demPoPricing';
import {
  fetchOrderBatches,
  type DemOrderBatch,
  type DemOrderBatchLineInput,
} from './demOrderBatches';
import type { DemCustomer, DemOrderChangeLog, DemOrderSource } from '../types/dem';

function navRound(num: number): number {
  return Number(Math.round(Number(`${num}e2`)) + 'e-2');
}

export type PendingOrderHeaderPatch = {
  customer_code: string;
  delivery_date: string;
  ref_no: string;
  remarks: string;
  contact_person: string;
  contact_no: string;
  email: string;
};

export type PendingOrderEditInput = {
  order_id: string;
  changed_by: string;
  /** Logged-in DEM actor ids (must be non-empty). */
  actorIds: string[];
  header: PendingOrderHeaderPatch;
  /** Replacement lines for batch 0 (must be unexported). */
  items: DemOrderBatchLineInput[];
};

export type DemOrderActivityEntry = {
  id: string;
  at: string;
  by: string;
  kind: 'created' | 'edit' | 'amend' | 'export' | 'checked';
  summary: string;
};

function normalizeActor(raw: string): string {
  return raw.trim().toLowerCase();
}

export function canEditPendingOrder(input: {
  status: string;
  batches?: DemOrderBatch[] | null;
  /** At least one resolved DEM actor id (logged-in portal user). */
  actorIds: string[];
}): boolean {
  if (input.status !== 'Pending') return false;
  if (!input.actorIds.some((id) => normalizeActor(id))) return false;
  const batches = [...(input.batches ?? [])].sort((a, b) => a.batch_no - b.batch_no);
  const batch0 = batches.find((b) => b.batch_no === 0) ?? batches[0];
  if (!batch0) return true; // legacy row without batches — allow
  return !batch0.exported_at;
}

function itemCodeKey(line: { item_code: string }): string {
  return line.item_code.trim().toUpperCase();
}

function formatItemRef(line: DemOrderBatchLineInput): string {
  const code = line.item_code.trim().toUpperCase() || '—';
  const qty = Number(line.qty);
  const uom = line.uom.trim() || 'PCS';
  if (Number.isFinite(qty) && qty > 0) return `${code} × ${qty} ${uom}`;
  return code;
}

function summarizeItemDiff(
  beforeItems: DemOrderBatchLineInput[],
  afterItems: DemOrderBatchLineInput[],
): string[] {
  const beforeBuckets = new Map<string, DemOrderBatchLineInput[]>();
  const afterBuckets = new Map<string, DemOrderBatchLineInput[]>();
  for (const line of beforeItems) {
    const key = itemCodeKey(line);
    const bucket = beforeBuckets.get(key) ?? [];
    bucket.push(line);
    beforeBuckets.set(key, bucket);
  }
  for (const line of afterItems) {
    const key = itemCodeKey(line);
    const bucket = afterBuckets.get(key) ?? [];
    bucket.push(line);
    afterBuckets.set(key, bucket);
  }

  const bits: string[] = [];
  const removedOnly: DemOrderBatchLineInput[] = [];
  const addedOnly: DemOrderBatchLineInput[] = [];

  const allCodes = new Set([...beforeBuckets.keys(), ...afterBuckets.keys()]);
  for (const code of allCodes) {
    const beforeList = [...(beforeBuckets.get(code) ?? [])];
    const afterList = [...(afterBuckets.get(code) ?? [])];
    const paired = Math.min(beforeList.length, afterList.length);
    for (let i = 0; i < paired; i += 1) {
      const prev = beforeList[i]!;
      const next = afterList[i]!;
      if (Number(prev.qty) !== Number(next.qty)) {
        bits.push(`Changed ${code} qty ${prev.qty} → ${next.qty}`);
      } else if (prev.uom.trim().toUpperCase() !== next.uom.trim().toUpperCase()) {
        bits.push(`Changed ${code} UOM ${prev.uom || '—'} → ${next.uom || '—'}`);
      }
    }
    for (let i = paired; i < beforeList.length; i += 1) removedOnly.push(beforeList[i]!);
    for (let i = paired; i < afterList.length; i += 1) addedOnly.push(afterList[i]!);
  }

  const replaceCount = Math.min(removedOnly.length, addedOnly.length);
  for (let i = 0; i < replaceCount; i += 1) {
    bits.push(`Replaced ${formatItemRef(removedOnly[i]!)} with ${formatItemRef(addedOnly[i]!)}`);
  }
  for (let i = replaceCount; i < removedOnly.length; i += 1) {
    bits.push(`Removed ${formatItemRef(removedOnly[i]!)}`);
  }
  for (let i = replaceCount; i < addedOnly.length; i += 1) {
    bits.push(`Added ${formatItemRef(addedOnly[i]!)}`);
  }

  return bits;
}

function summarizeEdit(
  beforeHeader: PendingOrderHeaderPatch & { customer_name?: string },
  afterHeader: PendingOrderHeaderPatch,
  beforeItems: DemOrderBatchLineInput[],
  afterItems: DemOrderBatchLineInput[],
): string {
  const bits: string[] = [];
  const beforeCust = beforeHeader.customer_code.trim();
  const afterCust = afterHeader.customer_code.trim();
  if (beforeCust !== afterCust) {
    bits.push(`Replaced customer ${beforeCust || '—'} → ${afterCust || '—'}`);
  }
  if (beforeHeader.delivery_date !== afterHeader.delivery_date) {
    bits.push(
      `Changed delivery date ${beforeHeader.delivery_date || '—'} → ${afterHeader.delivery_date || '—'}`,
    );
  }
  if (beforeHeader.ref_no.trim() !== afterHeader.ref_no.trim()) {
    bits.push(`Changed PO/ref ${beforeHeader.ref_no.trim() || '—'} → ${afterHeader.ref_no.trim() || '—'}`);
  }
  if (beforeHeader.remarks.trim() !== afterHeader.remarks.trim()) {
    bits.push('Changed remarks');
  }
  if (beforeHeader.contact_person.trim() !== afterHeader.contact_person.trim()) {
    bits.push('Changed contact person');
  }
  if (beforeHeader.contact_no.trim() !== afterHeader.contact_no.trim()) {
    bits.push('Changed contact no.');
  }
  if (beforeHeader.email.trim() !== afterHeader.email.trim()) {
    bits.push('Changed email');
  }

  bits.push(...summarizeItemDiff(beforeItems, afterItems));

  if (bits.length === 0) return 'Saved without field changes';
  if (bits.length === 1) return bits[0]!;
  return bits.join('; ');
}

async function recalcOrderTotal(
  customerCode: string,
  deliveryDate: string,
  lines: DemOrderBatchLineInput[],
): Promise<number> {
  if (!customerCode || lines.length === 0) return 0;
  const prices = await fetchPricesForItems(lines.map((line) => line.item_code));
  const customers: DemCustomer[] = [];
  const customer = await fetchDemCustomer(customerCode);
  if (customer) customers.push(customer);
  const mainCode = demMainCustomerCode(customerCode);
  if (mainCode && mainCode.toUpperCase() !== customerCode.toUpperCase()) {
    const parent = await fetchDemCustomer(mainCode);
    if (parent) customers.push(parent);
  }
  let total = 0;
  for (const line of lines) {
    const unit = getNavisionPrice(prices, {
      itemCode: line.item_code,
      custCode: customerCode,
      qty: line.qty,
      uom: line.uom,
      deliveryDate,
      customers,
    });
    total += navRound(line.qty * unit);
  }
  return navRound(total);
}

export async function insertOrderChangeLog(input: {
  order_id: string;
  changed_by: string;
  change_type: DemOrderChangeLog['change_type'];
  summary: string;
  before_json?: unknown;
  after_json?: unknown;
}): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db.from('order_change_log').insert({
    order_id: input.order_id.trim(),
    changed_by: input.changed_by.trim(),
    change_type: input.change_type,
    summary: input.summary.trim() || 'Updated',
    before_json: input.before_json ?? null,
    after_json: input.after_json ?? null,
  });
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchOrderChangeLogs(orderId: string): Promise<DemOrderChangeLog[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('order_change_log')
    .select('id, order_id, changed_at, changed_by, change_type, summary, before_json, after_json')
    .eq('order_id', orderId.trim())
    .order('changed_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: String(r.id ?? ''),
      order_id: String(r.order_id ?? ''),
      changed_at: String(r.changed_at ?? ''),
      changed_by: String(r.changed_by ?? ''),
      change_type: String(r.change_type ?? 'edit') as DemOrderChangeLog['change_type'],
      summary: String(r.summary ?? ''),
      before_json: r.before_json ?? null,
      after_json: r.after_json ?? null,
    };
  });
}

export async function buildOrderActivityTimeline(input: {
  order_id: string;
  created_by: string;
  created_at: string;
  checked_at?: string | null;
  checked_by?: string | null;
  batches?: DemOrderBatch[] | null;
}): Promise<DemOrderActivityEntry[]> {
  const [logs, nameMap] = await Promise.all([
    fetchOrderChangeLogs(input.order_id),
    fetchDemUserDisplayNameMap().catch(() => new Map<string, string>()),
  ]);
  const batches = input.batches?.length
    ? [...input.batches].sort((a, b) => a.batch_no - b.batch_no)
    : await fetchOrderBatches(input.order_id);

  const entries: DemOrderActivityEntry[] = [];

  entries.push({
    id: `created:${input.order_id}`,
    at: input.created_at,
    by: resolveDemDisplayUser(input.created_by, nameMap),
    kind: 'created',
    summary: 'Order created',
  });

  for (const batch of batches) {
    if (batch.batch_no > 0) {
      entries.push({
        id: `amend:${batch.id}`,
        at: batch.created_at,
        by: resolveDemDisplayUser(batch.created_by, nameMap),
        kind: 'amend',
        summary: batch.reason?.trim()
          ? `Add-on batch #${batch.batch_no}: ${batch.reason.trim()}`
          : `Add-on batch #${batch.batch_no}`,
      });
    }
    if (batch.exported_at) {
      entries.push({
        id: `export:${batch.id}`,
        at: batch.exported_at,
        by: resolveDemDisplayUser(batch.exported_by ?? '', nameMap),
        kind: 'export',
        summary:
          batch.batch_no === 0
            ? 'Exported original batch'
            : `Exported add-on batch #${batch.batch_no}`,
      });
    }
  }

  // Batch rows already cover amend/export — only surface field-edit logs here.
  for (const log of logs) {
    if (log.change_type === 'amend_batch') continue;
    entries.push({
      id: `log:${log.id}`,
      at: log.changed_at,
      by: resolveDemDisplayUser(log.changed_by, nameMap),
      kind: 'edit',
      summary: log.summary || 'Edited order',
    });
  }

  if (input.checked_at) {
    entries.push({
      id: `checked:${input.order_id}`,
      at: input.checked_at,
      by: resolveDemDisplayUser(input.checked_by ?? '', nameMap),
      kind: 'checked',
      summary: 'Marked NAV checked',
    });
  }

  return entries.sort((a, b) => {
    const ta = new Date(a.at).getTime();
    const tb = new Date(b.at).getTime();
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return tb - ta;
  });
}

export async function updatePendingOrder(input: PendingOrderEditInput): Promise<void> {
  assertDemActorReady(input.changed_by);
  const orderId = input.order_id.trim();
  if (!orderId) throw new Error('Order id is required.');

  const customerCode = input.header.customer_code.trim();
  if (!customerCode) throw new Error('Customer number is required.');

  const lines = input.items
    .map((line) => ({
      item_code: line.item_code.trim().toUpperCase(),
      description: line.description.trim(),
      qty: Number(line.qty),
      uom: line.uom.trim() || 'PCS',
    }))
    .filter((line) => line.item_code && Number.isFinite(line.qty) && line.qty > 0);

  if (lines.length === 0) {
    throw new Error('Add at least one item with quantity.');
  }

  const deliveryDate = input.header.delivery_date.trim().split('T')[0] ?? '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deliveryDate)) {
    throw new Error('Delivery date is required.');
  }

  const db = getDbSupabase('dem');
  const { data: orderRow, error: orderError } = await db
    .from('orders')
    .select(
      'order_id, status, customer_code, delivery_date, ref_no, remarks, contact_person, contact_no, email, created_by, source',
    )
    .eq('order_id', orderId)
    .maybeSingle();
  if (orderError) throw new Error(formatSupabaseError(orderError));
  if (!orderRow) throw new Error('Order not found.');

  const order = orderRow as Record<string, unknown>;
  const status = String(order.status ?? '');
  if (status !== 'Pending') {
    throw new Error('Only Pending orders can be edited. Use Add items for amendments after export.');
  }
  if (!input.actorIds.some((id) => normalizeActor(id))) {
    throw new Error('Sign in with a DEM account before editing orders.');
  }

  const batches = await fetchOrderBatches(orderId);
  const batch0 = batches.find((b) => b.batch_no === 0);
  if (!batch0) throw new Error('Original batch not found for this order.');
  if (batch0.exported_at) {
    throw new Error('Original batch already exported — use Add items instead.');
  }

  const { data: existingItemsRaw, error: itemsReadError } = await db
    .from('order_items')
    .select('item_code, description, qty, uom, batch_id')
    .eq('order_id', orderId)
    .eq('batch_id', batch0.id);
  if (itemsReadError) throw new Error(formatSupabaseError(itemsReadError));

  const beforeItems: DemOrderBatchLineInput[] = (existingItemsRaw ?? []).map((row) => {
    const r = row as Record<string, unknown>;
    return {
      item_code: String(r.item_code ?? '').trim(),
      description: String(r.description ?? '').trim(),
      qty: Number(r.qty ?? 0),
      uom: String(r.uom ?? 'PCS').trim() || 'PCS',
    };
  });

  const beforeHeader: PendingOrderHeaderPatch = {
    customer_code: String(order.customer_code ?? '').trim(),
    delivery_date: String(order.delivery_date ?? '').trim().split('T')[0] ?? '',
    ref_no: String(order.ref_no ?? '').trim(),
    remarks: String(order.remarks ?? '').trim(),
    contact_person: String(order.contact_person ?? '').trim(),
    contact_no: String(order.contact_no ?? '').trim(),
    email: String(order.email ?? '').trim(),
  };

  const afterHeader: PendingOrderHeaderPatch = {
    customer_code: customerCode,
    delivery_date: deliveryDate,
    ref_no: input.header.ref_no.trim(),
    remarks: input.header.remarks.trim(),
    contact_person: input.header.contact_person.trim(),
    contact_no: input.header.contact_no.trim(),
    email: input.header.email.trim(),
  };

  const totalAmount = await recalcOrderTotal(customerCode, deliveryDate, lines);

  const { error: headerError } = await db
    .from('orders')
    .update({
      customer_code: afterHeader.customer_code,
      delivery_date: afterHeader.delivery_date,
      ref_no: afterHeader.ref_no,
      remarks: afterHeader.remarks,
      contact_person: afterHeader.contact_person,
      contact_no: afterHeader.contact_no,
      email: afterHeader.email,
      total_amount: totalAmount,
      checked_at: null,
      checked_by: null,
    })
    .eq('order_id', orderId);
  if (headerError) throw new Error(formatSupabaseError(headerError));

  const { error: deleteError } = await db
    .from('order_items')
    .delete()
    .eq('order_id', orderId)
    .eq('batch_id', batch0.id);
  if (deleteError) throw new Error(formatSupabaseError(deleteError));

  const { error: insertError } = await db.from('order_items').insert(
    lines.map((line) => ({
      order_id: orderId,
      batch_id: batch0.id,
      item_code: line.item_code,
      description: line.description,
      qty: line.qty,
      uom: line.uom,
    })),
  );
  if (insertError) throw new Error(formatSupabaseError(insertError));

  const summary = summarizeEdit(beforeHeader, afterHeader, beforeItems, lines);
  await insertOrderChangeLog({
    order_id: orderId,
    changed_by: input.changed_by.trim(),
    change_type: 'edit',
    summary,
    before_json: { header: beforeHeader, items: beforeItems },
    after_json: { header: afterHeader, items: lines },
  });
}

export function parseDeliveryDateInput(displayOrIso: string): string {
  const value = displayOrIso.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
  }
  return '';
}

export function sourceDetailTitle(source: DemOrderSource, orderId: string): string {
  if (source === 'CallIn') return `Manual Order — ${orderId}`;
  return `Order details — ${orderId}`;
}
